import {Pie, mixins} from 'vue-chartjs'
import themedChart from '@/components/charts/mixins/themedChart'
import { cssVar } from '@/utils/cssVars'
const { reactiveProp } = mixins

function buildPalette() {
    return [
        cssVar('--accent-primary', '#339CFF'),
        cssVar('--accent-secondary', '#A529FF'),
        cssVar('--success', '#32D74B'),
        cssVar('--warning', '#FF9500'),
        cssVar('--danger', '#FF3B30')
    ];
}

function solidifyColor(c) {
    if (!c) return c;
    if (typeof c === 'string') {
        const m = c.match(/^rgba\s*\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)\s*\)$/i);
        if (m) return `rgba(${m[1]}, ${m[2]}, ${m[3]}, 1)`;
        return c;
    }
    return c;
}

// Strip cycle-prone properties and clone safely
function stripCycles(obj, seen = new WeakSet()) {
    if (!obj || typeof obj !== 'object') return obj;
    if (seen.has(obj)) return undefined;
    seen.add(obj);
    if (Array.isArray(obj)) return obj.map(v => stripCycles(v, seen));
    const out = {};
    const omit = new Set(['_meta','meta','chart','config','$context','_chart']);
    Object.keys(obj).forEach(k => {
        if (omit.has(k)) return;
        out[k] = stripCycles(obj[k], seen);
    });
    return out;
}
function safeCloneData(data) {
    try {
        if (typeof structuredClone === 'function') return structuredClone(data);
    } catch (e) {}
    try {
        return JSON.parse(JSON.stringify(stripCycles(data)));
    } catch (e) {
        const ds = Array.isArray(data && data.datasets) ? data.datasets.map(d => {
            const { _meta, meta, chart, config, $context, _chart, ...rest } = (d || {});
            return Object.assign({}, rest);
        }) : [];
        const labels = Array.isArray(data && data.labels) ? data.labels.slice() : [];
        return { datasets: ds, labels };
    }
}

function normalizeSegments(data) {
    if (!data) return data;
    const palette = buildPalette();
    const labelsLen = Array.isArray(data.labels) ? data.labels.length : 0;
    if (!Array.isArray(data.datasets)) return data;
    data.datasets = data.datasets.map((ds) => {
        const copy = Object.assign({}, ds);
        const segCount = (Array.isArray(copy.data) && copy.data.length) || labelsLen || 0;
        if (!copy.backgroundColor) {
            copy.backgroundColor = Array.from({ length: segCount }, (_, i) => palette[i % palette.length]);
        } else if (typeof copy.backgroundColor === 'string') {
            copy.backgroundColor = Array.from({ length: segCount }, () => solidifyColor(copy.backgroundColor));
        } else if (Array.isArray(copy.backgroundColor)) {
            copy.backgroundColor = copy.backgroundColor.map(solidifyColor);
            if (segCount && copy.backgroundColor.length < segCount) {
                while (copy.backgroundColor.length < segCount) {
                    copy.backgroundColor.push(palette[copy.backgroundColor.length % palette.length]);
                }
            }
        }
        if (!copy.borderColor) {
            copy.borderColor = Array.isArray(copy.backgroundColor) ? copy.backgroundColor.slice() : copy.backgroundColor;
        } else if (typeof copy.borderColor === 'string') {
            copy.borderColor = Array.from({ length: segCount }, () => solidifyColor(copy.borderColor));
        } else if (Array.isArray(copy.borderColor)) {
            copy.borderColor = copy.borderColor.map(solidifyColor);
        }
        return copy;
    });
    return data;
}

export default {
    extends: Pie,
    mixins: [reactiveProp, themedChart],
    props: {
        options : { default: ()=>{ return {
            maintainAspectRatio : false,
            legend: { // internal legend disabled in favor of external left legend
                display: false
            },
            legendCallback: (chart) => {
                const ds = (chart.data && chart.data.datasets && chart.data.datasets[0]) || { backgroundColor: [] };
                const labels = (chart.data && chart.data.labels) || [];
                const html = [];
                html.push('<ul class="ln-legend-list">');
                labels.forEach((label, i) => {
                    const color = ds.backgroundColor[i] || 'var(--accent-primary, #339CFF)';
                    html.push(
                        '<li class="ln-legend-item">' +
                        '<span class="ln-legend-swatch" style="background:' + color + '"></span>' +
                        '<span class="ln-legend-label">' + (label || '&nbsp;') + '</span>' +
                        '</li>'
                    );
                });
                html.push('</ul>');
                return html.join('');
            }
        }; } },
        height : { default: ()=>{ return 400; } }
    },
    data () {
        return {
            // inline style objects to avoid adding new CSS files; respects mobile stacking
            $styles: {
                row: { display: 'flex', alignItems: 'flex-start', width: '100%', overflowX: 'hidden' },
                legendCol: { flex: '0 0 42%', maxHeight: '420px', overflow: 'auto' },
                canvasCol: { flex: '1 1 58%', minHeight: '320px', position: 'relative' }
            }
        }
    },
    watch: {
        chartData (newData) {
            const safe = newData ? safeCloneData(newData) : { datasets: [], labels: [] }
            this._renderWithOptions(normalizeSegments(safe))
        },
        options: {
            deep: true,
            handler() {
                const safe = this.chartData ? safeCloneData(this.chartData) : { datasets: [], labels: [] }
                this._renderWithOptions(normalizeSegments(safe))
            }
        }
    },
    mounted () {
        const safe = this.chartData ? safeCloneData(this.chartData) : { datasets: [], labels: [] }
        this._renderWithOptions(normalizeSegments(safe))
        // basic responsive tweak: stack vertically on small screens
        if (typeof window !== 'undefined') {
            const mq = window.matchMedia('(max-width: 768px)')
            const apply = () => {
                const row = this.$refs && this.$refs.row
                if (!row) return
                if (mq.matches) {
                    row.style.flexDirection = 'column'
                    this.$refs.legend && (this.$refs.legend.style.maxHeight = 'none')
                } else {
                    row.style.flexDirection = 'row'
                    this.$refs.legend && (this.$refs.legend.style.maxHeight = '420px')
                }
            }
            try { mq.addListener ? mq.addListener(apply) : mq.addEventListener('change', apply) } catch (e) {}
            this.$nextTick(apply)
        }
    },
    beforeDestroy() {
        if (this.$data && this.$data._chart && this.$data._chart.destroy) {
            try { this.$data._chart.destroy(); } catch (e) {}
        }
    },
    methods: {
        _mergedOptions() {
            // Merge our default options with incoming options, then apply themed options
            const base = this.$options.props.options.default()
            const incoming = this.options || {}
            const merged = Object.assign({}, base, incoming)
            // Enforce universal external legend: always disable internal legend
            merged.legend = Object.assign({}, merged.legend, { display: false })
            // Ensure legendCallback exists for external legend HTML
            if (!merged.legendCallback) merged.legendCallback = base.legendCallback
            return this.themedOptions(merged)
        },
        _renderWithOptions(data) {
            this.renderChart(data, this._mergedOptions())
            this._renderExternalLegend()
        },
        _renderExternalLegend() {
            if (!this.$data || !this.$data._chart || !this.$refs || !this.$refs.legend) return
            try { this.$refs.legend.innerHTML = this.$data._chart.generateLegend() } catch (e) {}
        }
    },
    render(h) {
        // Custom layout: legend left, chart right
        return h('div', { ref: 'row', class: 'ln-chart-row', style: this.$data.$styles.row }, [
            h('div', { ref: 'legend', class: 'ln-legend-col', style: this.$data.$styles.legendCol }),
            h('div', { class: 'ln-canvas-col', style: this.$data.$styles.canvasCol }, [
                h('canvas', { ref: 'canvas' })
            ])
        ])
    }
}
